import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { noop } from 'ramda-extension';

import { getLabelMessage } from '@gef-ui/localization/messages';
import Xmark from '@gef-ui/material-icons/Xmark';

const allowedDescriptionName = /^[^:;<>$/'"]*$/;

const CardEditable = ({
	text = '',
	header,
	type = 'info',
	placeholder,
	onClose = noop,
	maxLength = 30,
	onChange = noop,
}) => {
	const textFieldRef = useRef(null);
	const { mode: appTheme } = useColorScheme();

	const [showTooltipHover, setShowTooltipHover] = useState(false);
	const [fieldValue, setFieldValue] = useState(text);
	const [isValid, setValid] = useState(true);

	const compareSize = useCallback(() => {
		const compare = textFieldRef.current.scrollWidth > textFieldRef.current.clientWidth;
		setShowTooltipHover(compare);
	}, []);

	// compare once and add resize listener on "componentDidMount"
	useEffect(() => {
		compareSize();
		window.addEventListener('resize', compareSize);

		return () => window.removeEventListener('resize', compareSize);
	}, [compareSize, fieldValue]);

	const handleChange = useCallback(
		(e) => {
			const { value } = e.target;
			setFieldValue(value);
			const isValueValid = value.match(allowedDescriptionName);
			setValid(isValueValid);
			onChange(value);
		},
		[onChange],
	);

	return (
		<Card
			sx={{
				borderRadius: '5px',
				boxShadow: '0 0 13px 0 rgba(0,0,0,.24)',
				backgroundColor: appTheme === 'light' ? 'common.white' : 'background.300',
			}}
		>
			<CardHeader
				sx={{
					py: 0,
					px: 1,
					backgroundColor: type === 'error' ? 'primary.main' : 'info.shades.50p',
					// needed because of typography noWrap
					'& .MuiCardHeader-content': {
						width: '100%',
					},
				}}
				title={
					<Stack
						direction="row"
						sx={{
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Box
							sx={{
								color: 'common.white',
								width: '80%',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							{header}
						</Box>
						<IconButton size="small" onClick={onClose}>
							<Xmark sx={{ color: 'common.white' }} />
						</IconButton>
					</Stack>
				}
			/>
			<CardContent sx={{ p: 1 }}>
				<Tooltip title={fieldValue} interactive disableFocusListener disableHoverListener={!showTooltipHover}>
					<TextField
						inputRef={textFieldRef}
						fullWidth
						variant="standard"
						autoComplete="off"
						value={fieldValue}
						onChange={handleChange}
						error={!isValid}
						helperText={!isValid && getLabelMessage('validation:allowedCharactersDescription')}
						placeholder={placeholder || getLabelMessage('card:placeholder')}
						slotProps={{
							htmlInput: {
								maxLength,
							},
						}}
						sx={{
							'& .MuiInputBase-input': {
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							},
						}}
					/>
				</Tooltip>
			</CardContent>
		</Card>
	);
};

CardEditable.propTypes = {
	header: PropTypes.any,
	maxLength: PropTypes.number,
	onChange: PropTypes.func,
	onClose: PropTypes.func,
	placeholder: PropTypes.string,
	text: PropTypes.string,
	type: PropTypes.string,
};

export default CardEditable;
